import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import * as courseTimerActions from 'store/timer/actions';
import { TimerStatus } from 'constants/timer';
import {
  getTimerEnabled,
  isQuestionPoolEnabled,
  getQuestionPoolSize
} from 'store/settings/selectors';
import { RootAppState } from '../../../../store/types.js';
import {
  isQuestionAnswered,
  isQuestionAnsweredCorrectly,
  isFeedbackAnimating,
  isAllAffectedQuestionsAnswered
} from '../../../../store/questions/selectors';
import { RESULTS_PATH } from '../../../../constants/navigation';
import { localize } from '../../../../core/localization';
import * as questionActions from '../../../../store/questions/actions';
import { BUTTON, TOOLTIP } from '../../../../constants/components';
import { INFORMATION_CONTENT, SCENARIO } from '../../../../constants/questionTypes';
import { Button, StatusTooltip } from '../../../../components/common';
import { isAttemptSubmitted } from '../../../../store/course/selectors';
import { ActionBlocks, ActionBlocksProps } from './ActionsBlock';
import SubmitOnceButton from '../../../../components/submitOnce';
import {
  ActionButtonsContainer,
  SurveyHint,
  SubmitOnceButtonWrapper,
  NavButtonsBlock,
  SubmitOnceButtonMobileWrapper
} from './ActionsBlock.styled';

interface ActionsBlockSubmitOnceProps extends ActionBlocksProps {
  overallNumberOfQuestions: number;
  courseQuestionIndex: number;
  isEveryAffectedQuestionsAnswered?: boolean;
  showSubmitButton?: boolean;
  isCourseSubmitted?: boolean;
  isLastPage?: boolean;
  navigateToUrl(url: string): void;
  questionPoolSize?: number;
  isQuestionPool?: boolean;
  timerActions?: { [key: string]: any };
  isTimerEnabled?: boolean;
}

export class ActionsBlockSubmitOnce extends ActionBlocks<ActionsBlockSubmitOnceProps> {
  constructor(props: ActionsBlockSubmitOnceProps) {
    super(props);
    this.state = {
      isJustSubmitted: false,
      showTooltip: false,
      isInformationContent: this.props.question.type === INFORMATION_CONTENT,
      canGoNext: false
    };
  }

  render() {
    const { isInformationContent } = this.state;
    const {
      question,
      isCourseSubmitted,
      courseQuestionIndex,
      overallNumberOfQuestions,
      isCourseOnOnePage
    } = this.props;
    const { isSurvey } = question;
    const isLastPage = courseQuestionIndex + 1 === overallNumberOfQuestions;
    return (
      <ActionButtonsContainer isHidden={false}>
        {!isCourseSubmitted && !isInformationContent && (
          <React.Fragment>
            {question.type === SCENARIO ? (
              <StatusTooltip
                popoverPosition={'top'}
                layout={TOOLTIP.INCORRECT}
                showTooltip={this.state.showTooltip}
                message={localize('[scenario tooltip text]')}
                popoverButton={
                  <Button
                    tabIndex={0}
                    onClick={this.next}
                    onBlur={this.hidePopover}
                    data-test="button-next"
                    layout={this.questionNextButtonLayout()}
                  >
                    {localize('[next]')}
                  </Button>
                }
              />
            ) : (
              !isLastPage &&
              !isCourseOnOnePage && (
                <React.Fragment>
                  <Button
                    tabIndex={0}
                    onClick={this.next}
                    data-test="button-next"
                    layout={this.questionNextButtonLayout()}
                  >
                    {localize('[next]')}
                  </Button>
                  {isSurvey && <SurveyHint>{localize('[survey question hint]')}</SurveyHint>}
                </React.Fragment>
              )
            )}
          </React.Fragment>
        )}
        {!isLastPage &&
          !isCourseSubmitted &&
          isInformationContent &&
          !isCourseOnOnePage &&
          this.renderNavButtonsBlock()}
        {isCourseSubmitted && this.renderNavButtonsBlock()}
        {this.renderSubmitAtOnceButtonWrapper()}
      </ActionButtonsContainer>
    );
  }

  questionNextButtonLayout() {
    const {
      question: { isAnswered },
      isEveryAffectedQuestionsAnswered
    } = this.props;
    if (isEveryAffectedQuestionsAnswered) {
      return BUTTON.LAYOUT.SECONDARY;
    }
    if (isAnswered) {
      return BUTTON.LAYOUT.PRIMARY;
    }
    return BUTTON.LAYOUT.SECONDARY;
  }

  canTryAgainQuestion() {
    const { question, isCourseSubmitted } = this.props;

    return (
      question.type !== INFORMATION_CONTENT &&
      isCourseSubmitted &&
      question.isAnswered &&
      !question.isAnsweredCorrectly &&
      !question.isSurvey
    );
  }

  renderSubmitAtOnceButtonWrapper() {
    const {
      showSubmitButton,
      courseQuestionIndex,
      isEveryAffectedQuestionsAnswered,
      isCourseOnOnePage,
      overallNumberOfQuestions,
      question
    } = this.props;
    const isLastQuestion = courseQuestionIndex + 1 === overallNumberOfQuestions;
    const shouldAddAdditionalMargin = isLastQuestion && question.type === INFORMATION_CONTENT;

    const isActive =
      isLastQuestion ||
      (isCourseOnOnePage
        ? isEveryAffectedQuestionsAnswered && isLastQuestion
        : isEveryAffectedQuestionsAnswered);

    return (
      isActive &&
      showSubmitButton && (
        <SubmitOnceButtonMobileWrapper>
          <SubmitOnceButtonWrapper
            className="submit-button-wrapper"
            data-test="submit-button-wrapper"
            shouldAddAdditionalMargin={shouldAddAdditionalMargin}
          >
            <SubmitOnceButton
              onClick={this.submitAllAnswers}
              isActive={isActive && showSubmitButton}
              tabIndex={0}
            />
          </SubmitOnceButtonWrapper>
        </SubmitOnceButtonMobileWrapper>
      )
    );
  }

  nextButtonLayout() {
    const { isCourseSubmitted, isEveryAffectedQuestionsAnswered } = this.props;
    return isCourseSubmitted || isEveryAffectedQuestionsAnswered
      ? BUTTON.LAYOUT.SECONDARY
      : BUTTON.LAYOUT.PRIMARY;
  }

  renderNavButtonsBlock() {
    const {
      questionIndex,
      questionCountInSection,
      goToNextPage,
      isQuestionPool,
      questionPoolSize,
      isCourseOnOnePage
    } = this.props;
    const isLastPageInSection =
      questionIndex + 1 === (isQuestionPool ? questionPoolSize : questionCountInSection);
    const canGoNext = this.canGoToNextQuestion();
    return (
      !isCourseOnOnePage &&
      canGoNext && (
        <NavButtonsBlock tabIndex={-1}>
          <Button
            data-test="button-next"
            layout={this.nextButtonLayout()}
            onClick={goToNextPage}
            tabIndex={0}
            suffixIcon="arrow-down"
          >
            {this.getNextNavigationText(isLastPageInSection)}
          </Button>
        </NavButtonsBlock>
      )
    );
  }

  next = () => {
    const { goToNextPage, courseQuestionIndex, overallNumberOfQuestions } = this.props;

    const isLastPage = courseQuestionIndex + 1 === overallNumberOfQuestions;

    if (isLastPage) {
      this.props.actions && this.props.actions.submitAllAtOnce();
      goToNextPage();
    } else {
      this.setState({
        canGoNext: true
      });
      goToNextPage();
    }
  };

  submitAllAnswers = () => {
    const { isTimerEnabled, actions, timerActions, navigateToUrl } = this.props;
    if (timerActions && isTimerEnabled) {
      timerActions.setTimerStatus(TimerStatus.STOPPED);
      timerActions.updateTimerProgress();
    }
    if (actions !== undefined) {
      actions.submitAllAtOnce();
    }
    navigateToUrl(RESULTS_PATH);
  };
}

function mapStateToProps(state: RootAppState, ownProps: ActionsBlockSubmitOnceProps) {
  const questionId = ownProps.question.id;
  return {
    isAnswered: isQuestionAnswered(state, questionId),
    isAnsweredCorrectly: isQuestionAnsweredCorrectly(state, questionId),
    feedbackAnimated: isFeedbackAnimating(state, questionId),
    isCourseSubmitted: isAttemptSubmitted(state),
    showSubmitButton: !isAttemptSubmitted(state),
    isEveryAffectedQuestionsAnswered: isAllAffectedQuestionsAnswered(state),
    questionPoolSize: getQuestionPoolSize(state),
    isQuestionPool: isQuestionPoolEnabled(state),
    isTimerEnabled: getTimerEnabled(state)
  };
}

function mapDispatchToProps(dispatch: Dispatch) {
  return {
    actions: bindActionCreators(questionActions, dispatch),
    timerActions: bindActionCreators(courseTimerActions, dispatch)
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(ActionsBlockSubmitOnce);
